import { useAuth } from "@/contexts/AuthContext";

/**
 * Hook for getting the effective user ID based on active profile selection.
 * Centralizes logic for determining which profile's data to access in shared-email family accounts.
 *
 * @returns {{ effectiveUserId: string | null, isActingAsChild: boolean, activeProfileId: string | null, parentUserId: string | null, isManagedChild: boolean }}
 * - `effectiveUserId` — the currently selected profile ID (child or self)
 * - `isActingAsChild` — true when parent is viewing a child profile
 * - `parentUserId` — the authenticated user's own ID
 * - `isManagedChild` — true for profiles created by a parent (no auth.users entry)
 */
export function useActiveProfile() {
  const { user, profile, activeProfileId } = useAuth();
  
  // The effective user ID is the active profile (child) or fallback to auth user
  const effectiveUserId = activeProfileId || user?.id || null;
  
  // Whether we're currently acting as a child profile
  const isActingAsChild = Boolean(
    activeProfileId && 
    user?.id && 
    activeProfileId !== user.id
  );
  
  // The auth user's ID (parent)
  const parentUserId = user?.id || null;
  
  // Whether this is a managed child profile (no auth.users entry)
  const isManagedChild = Boolean(profile?.is_managed_child);
  
  return { 
    effectiveUserId, 
    isActingAsChild, 
    activeProfileId,
    parentUserId,
    isManagedChild
  };
}
